import { F, STATE_STRIDE } from '../simulation/constants.ts';
import type { TerrainParams, TerrainPreset } from '../config.ts';
import { clamp, fbm2, hash2f, lerp, ridged2, smoothstep, warp2 } from './noise.ts';
/* -------------------------------------------------------------------------- */
/* Packed state accessors                                                     */
/* -------------------------------------------------------------------------- */

export function stateIndex(i: number, j: number, width: number): number {
  return (j * width + i) * STATE_STRIDE;
}

export function getField(data: Float32Array, i: number, j: number, w: number, field: number): number {
  return data[(j * w + i) * STATE_STRIDE + field];
}

/* -------------------------------------------------------------------------- */
/* Terrain generation                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Material derivation.
 *
 * Once a normalised elevation `h01` and a pair of low-frequency noise fields
 * are known, every other initial state field follows deterministically, which is
 * what makes a seed reproducible.
 *
 * The column model is:
 *
 *     height  = bedrock + soil                    (exact invariant)
 *     bedrock = elevation of the unweathered rock
 *     soil    = thickness of the mobile regolith on top of it
 *
 * Keeping that identity exact everywhere means the *solid mass* of a cell is
 * simply `height`, and every process can be checked for conservation against
 * one number. `soil` is what the erosion and wind passes are allowed to move;
 * `bedrock` is the floor they cannot reach until the regolith is stripped.
 */
function deriveMaterials(
  height: number,
  h01: number,
  bandNoise: number,
  patchNoise: number,
): { hardness: number; bedrock: number; soil: number } {
  // Sedimentary banding: alternating hard/soft strata exposed by elevation.
  const strata = Math.sin((h01 * 9.0 + bandNoise * 2.2) * Math.PI);
  // hardness 0.25 (soft) .. 3.0 (very hard)
  const hardness = clamp(1.55 + strata * 0.75 + patchNoise * 0.85, 0.25, 3.0);
  // Valley floors and low ground collect a deep regolith cover; steep high
  // ground is left as bare rock.
  const lowland = 1 - smoothstep(0.18, 0.72, h01);
  let soil = clamp(0.6 + lowland * 3.0 + patchNoise * 1.2, 0.15, 8);
  // Never let the regolith eat the whole column.
  soil = Math.min(soil, height * 0.6 + 1e-3);
  const bedrock = clamp(height - soil, 0, height);
  return { hardness, bedrock, soil: height - bedrock };
}

/** Normalised (0..1) elevation for a preset at domain coords (u, v) in 0..1. */
function elevation(preset: TerrainPreset, u: number, v: number, p: TerrainParams): number {
  const s = p.scale;
  const seed = p.seed;
  const gain = p.roughness;
  const octaves = 7;
  const warped = warp2(u * s, v * s, seed, p.warp * s, 0.6);
  const wx = warped[0];
  const wy = warped[1];

  switch (preset) {
    case 'mountains': {
      // Ridged base * broad fBm mask: ranges rise out of a low plain instead
      // of tiling the whole domain with equally tall peaks.
      const ridge = ridged2(wx, wy, seed, { octaves, gain, frequency: 1, lacunarity: 2.03 });
      const mask = fbm2(wx * 0.45 + 3.1, wy * 0.45 - 2.7, seed + 313, {
        octaves: 4,
        gain: 0.55,
        frequency: 1,
      });
      const massif = smoothstep(-0.35, 0.55, mask);
      const detail = fbm2(wx * 3.1, wy * 3.1, seed + 77, { octaves: 5, gain: gain * 0.9, frequency: 1 });
      let h = Math.pow(clamp(ridge, 0, 1), p.sharpness) * (0.30 + 0.70 * massif);
      h += detail * 0.075;
      // Low-frequency valley incision gives the drainage network a head start.
      const valleys = ridged2(wx * 0.5 - 8.3, wy * 0.5 + 4.7, seed + 909, {
        octaves: 3,
        gain: 0.5,
        frequency: 1,
      });
      h *= 1 - 0.35 * Math.pow(clamp(valleys, 0, 1), 1.5);
      return clamp(h, 0, 1);
    }

    case 'rollingHills': {
      const base = fbm2(wx, wy, seed, { octaves, gain, frequency: 1, lacunarity: 2.01 });
      const rounded = billowLocal(wx * 1.7, wy * 1.7, seed + 41, gain);
      return clamp(0.45 + base * 0.42 + rounded * 0.22, 0, 1);
    }

    case 'plateau': {
      const base = fbm2(wx * 0.6, wy * 0.6, seed, { octaves: 4, gain: 0.45, frequency: 1 });
      // Hard flat top with a cliffed rim.
      const top = smoothstep(-0.12, 0.16, base);
      const table = Math.pow(top, 0.35);
      // Gullied rim so the plateau edge is not a perfect wall.
      const scarp = ridged2(wx * 2.4, wy * 2.4, seed + 611, { octaves: 4, gain: gain * 0.8, frequency: 1 });
      const h = table * (0.72 + 0.28 * (1 - Math.pow(clamp(scarp, 0, 1), 2.0)));
      return clamp(h, 0, 1);
    }

    case 'canyon': {
      const base = fbm2(wx * 0.6, wy * 0.6, seed, { octaves: 4, gain: 0.45, frequency: 1 });
      const table = Math.pow(smoothstep(-0.12, 0.16, base), 0.35);
      // Meandering river centreline: v = centre(u) + noise. Distance from the
      // channel carves a canyon whose walls get the upper strata exposed.
      const centre =
        0.5 +
        0.22 * Math.sin(u * Math.PI * 1.6 + seed * 0.017) +
        fbm2(u * 1.6, 17.3, seed + 8081, { octaves: 3, gain: 0.5, frequency: 1 }) * 0.16;
      const d = Math.abs(v - centre);
      const halfWidth = 0.055 + 0.03 * fbm2(u * 3.0, 5.1, seed + 404, { octaves: 3, gain: 0.5, frequency: 1 });
      const incise = 1 - smoothstep(0, halfWidth, d);
      // Inner gorge: a narrower, deeper slot inside the main canyon.
      const inner = 1 - smoothstep(0, halfWidth * 0.35, d);
      const walls = ridged2(wx * 2.6, wy * 2.6, seed + 222, { octaves: 4, gain: gain * 0.8, frequency: 1 });
      let h = table * (0.78 + 0.22 * (1 - Math.pow(clamp(walls, 0, 1), 2.2)));
      h *= 1 - 0.82 * incise;
      h *= 1 - 0.45 * inner;
      return clamp(h, 0, 1);
    }

    case 'dunes': {
      // Anisotropic ripples: a travelling wave train modulated by fBm, so the
      // wind-erosion pass has something to organise rather than flat sand.
      const bed = fbm2(wx * 0.5, wy * 0.5, seed, { octaves: 4, gain: 0.5, frequency: 1 });
      const phase = (wx * 0.85 + wy * 1.35 + fbm2(wx, wy, seed + 55, { octaves: 4, gain: 0.55, frequency: 1 }) * 1.1) * Math.PI * 3;
      const ripple = 0.5 + 0.5 * Math.sin(phase);
      const asym = Math.pow(ripple, 1.7); // steeper lee side
      return clamp(0.35 + bed * 0.25 + asym * 0.42, 0, 1);
    }

    case 'flat':
    default: {
      const tilt = 0.06 * (1 - v);
      const micro = fbm2(wx * 4, wy * 4, seed, { octaves: 4, gain: 0.35, frequency: 1 }) * 0.02;
      return clamp(0.30 + tilt + micro, 0, 1);
    }
  }
}

/** Billowy variant local to this module (keeps noise.ts surface tight). */
function billowLocal(x: number, y: number, seed: number, gain: number): number {
  let amp = 1;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < 5; i++) {
    // |perlin| via the fbm2 path would fold the whole octave stack; do it here
    // octave by octave instead.
    const n = fbm2(x * Math.pow(2.01, i), y * Math.pow(2.01, i), seed + i * 3253, {
      octaves: 1,
      gain,
      frequency: 1,
      rotate: 0,
    });
    sum += amp * Math.abs(n);
    norm += amp;
    amp *= gain;
  }
  return norm > 0 ? sum / norm : 0;
}

export interface TerrainGenerationOptions {
  /** Deterministic 0..1 erosion-style noise applied to the ridge pattern. */
  onProgress?: (fraction: number, label: string) => void;
}

/**
 * Generate the initial simulation state on the CPU.
 *
 * Why CPU: the generator runs once per "Regenerate", is trivially testable
 * offline, and needs higher-quality f64 noise than a shader would give. The
 * packed state is uploaded with a single `queue.writeBuffer`, so this costs one
 * transfer per regeneration — not per tick.
 *
 * @returns packed state, `STATE_STRIDE` floats per cell, row-major.
 */
export function generateTerrain(p: TerrainParams, opts: TerrainGenerationOptions = {}): Float32Array {
  const w = p.resolution;
  const data = new Float32Array(w * w * STATE_STRIDE);
  const inv = 1 / w;

  for (let j = 0; j < w; j++) {
    const v = (j + 0.5) * inv;
    for (let i = 0; i < w; i++) {
      const u = (i + 0.5) * inv;
      let h01 = elevation(p.preset, u, v, p);

      // Very low frequency noise still adds "where the range sits" variation.
      const bandNoise = fbm2(u * 3.2, v * 3.2, p.seed + 5150, { octaves: 4, gain: 0.5, frequency: 1 });
      const patchNoise = fbm2(u * 6.5 - 11.2, v * 6.5 + 4.4, p.seed + 6161, {
        octaves: 3,
        gain: 0.5,
        frequency: 1,
      });

      h01 = clamp(h01 + bandNoise * 0.015, 0, 1);
      const height = h01 * p.height;
      const { hardness, bedrock, soil } = deriveMaterials(height, h01, bandNoise, patchNoise);

      const b = stateIndex(i, j, w);
      data[b + F.height] = height;
      data[b + F.bedrock] = bedrock;
      data[b + F.soil] = soil;
      data[b + F.water] = 0;
      data[b + F.sediment] = 0;
      data[b + F.velU] = 0;
      data[b + F.velV] = 0;
      data[b + F.fluxX] = 0;
      data[b + F.fluxY] = 0;
      data[b + F.erosion] = 0;
      data[b + F.deposition] = 0;
      data[b + F.hardness] = hardness;
      data[b + F.flowAccum] = 1;
      data[b + F.groundwater] = 0;
    }
    if (opts.onProgress && (j & 31) === 31) opts.onProgress((j + 1) / w, 'Generating terrain');
  }
  opts.onProgress?.(1, 'Generating terrain');
  return data;
}

/** Small helper used by the brush: deterministic speckle for sediment sprays. */
export function speckle(i: number, j: number, seed: number): number {
  return hash2f(i * 7 + 1, j * 13 + 3, seed);
}

export { lerp };
